import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps, CircularProgress, IconButton, Fab, SxProps, Theme, useTheme } from '@mui/material';
import { colors } from '@constants/colors';
import { fontSizes, borderRadius, fontWeights } from '@constants/sizes';
// Button Types
export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'outline'
    | 'ghost'
    | 'text'
    | 'danger'
    | 'success'
    | 'warning'
    | 'info';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type ButtonType = 'button' | 'icon' | 'fab' | 'floating';

// Base Button Props
export interface BaseButtonProps extends Omit<MuiButtonProps, 'variant' | 'size'> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    buttonType?: ButtonType;
    loading?: boolean;
    loadingText?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
    rounded?: boolean;
    shadow?: boolean;
    gradient?: boolean;
    animated?: boolean;
}

// Size configurations
const getSizeConfig = (size: ButtonSize) => {
    const configs = {
        xs: { minHeight: 24, padding: '4px 8px', fontSize: fontSizes.body.small, iconSize: 16 },
        sm: { minHeight: 32, padding: '6px 12px', fontSize: fontSizes.body.medium, iconSize: 18 },
        md: { minHeight: 40, padding: '8px 16px', fontSize: fontSizes.body.large, iconSize: 20 },
        lg: { minHeight: 48, padding: '12px 24px', fontSize: fontSizes.subheading.medium, iconSize: 24 },
        xl: { minHeight: 56, padding: '16px 32px', fontSize: fontSizes.subheading.large, iconSize: 28 },
    };
    return configs[size];
};

// Variant styles
const getVariantStyles = (variant: ButtonVariant, gradient?: boolean, theme?: Theme) => {
    const baseStyles = {
        textTransform: 'none' as const,
        fontWeight: fontWeights.medium,
        borderRadius: borderRadius.medium,
        transition: 'all 0.2s ease-in-out',
        position: 'relative' as const,
        overflow: 'hidden' as const,
    };

    const variants = {
        primary: {
            ...baseStyles,
            backgroundColor: gradient ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : (theme?.palette.primary.main || colors.brand.primary),
            color: theme?.palette.primary.contrastText || colors.text.primary,
            border: 'none',
            '&:hover': {
                backgroundColor: gradient ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : (theme?.palette.primary.dark || colors.brand.primary),
                transform: 'none',
                boxShadow: 'none',
            },
            '&:active': {
                transform: 'translateY(0)',
                backgroundColor: theme?.palette.primary.dark || colors.interactive.active.primary,
            },
        },
        secondary: {
            ...baseStyles,
            backgroundColor: theme?.palette.secondary.main || colors.brand.secondary,
            color: theme?.palette.secondary.contrastText || colors.text.secondary,
            border: `1px solid ${theme?.palette.divider || colors.border.secondary}`,
            '&:hover': {
                backgroundColor: theme?.palette.secondary.dark || colors.brand.secondary,
                transform: 'none',
                boxShadow: 'none',
            },
        },
        tertiary: {
            ...baseStyles,
            backgroundColor: theme?.palette.action.hover || colors.background.gray,
            color: theme?.palette.text.secondary || colors.text.secondary,
            border: `1px solid ${theme?.palette.divider || colors.border.muted}`,
            '&:hover': {
                backgroundColor: theme?.palette.action.selected || colors.background.gray,
                transform: 'none',
            },
        },
        outline: {
            ...baseStyles,
            backgroundColor: 'transparent',
            color: theme?.palette.primary.main || colors.brand.primary,
            border: `2px solid ${theme?.palette.primary.main || colors.brand.primary}`,
            '&:hover': {
                backgroundColor: theme?.palette.primary.main || colors.brand.primary,
                color: theme?.palette.primary.contrastText || colors.text.primary,
                transform: 'none',
                boxShadow: 'none',
            },
        },
        ghost: {
            ...baseStyles,
            backgroundColor: 'transparent',
            color: colors.brand.primary,
            border: 'none',
            '&:hover': {
                backgroundColor: 'transparent',
                transform: 'none',
            },
        },
        text: {
            ...baseStyles,
            backgroundColor: 'transparent',
            color: colors.brand.primary,
            border: 'none',
            padding: '4px 8px',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        danger: {
            ...baseStyles,
            backgroundColor: colors.status.error,
            color: colors.text.primary,
            border: 'none',
            '&:hover': {
                backgroundColor: colors.status.error,
                opacity: 1,
                transform: 'none',
                boxShadow: 'none',
            },
        },
        success: {
            ...baseStyles,
            backgroundColor: colors.status.success,
            color: colors.text.primary,
            border: 'none',
            '&:hover': {
                backgroundColor: colors.status.success,
                opacity: 1,
                transform: 'none',
                boxShadow: 'none',
            },
        },
        warning: {
            ...baseStyles,
            backgroundColor: colors.status.warning,
            color: colors.text.primary,
            border: 'none',
            '&:hover': {
                backgroundColor: colors.status.warning,
                opacity: 1,
                transform: 'none',
                boxShadow: 'none',
            },
        },
        info: {
            ...baseStyles,
            backgroundColor: colors.status.info,
            color: colors.text.primary,
            border: 'none',
            '&:hover': {
                backgroundColor: colors.status.info,
                opacity: 1,
                transform: 'none',
                boxShadow: 'none',
            },
        },
    };

    return variants[variant];
};

// Helper function to get button styles
const getButtonStyles = (
    buttonVariant: ButtonVariant,
    buttonSize: ButtonSize,
    rounded: boolean,
    shadow: boolean,
    gradient: boolean,
    animated: boolean,
    theme?: Theme
): SxProps<Theme> => {
    const sizeConfig = getSizeConfig(buttonSize);
    const variantStyles = getVariantStyles(buttonVariant, gradient, theme);

    return {
        ...variantStyles,
        minHeight: sizeConfig.minHeight,
        padding: sizeConfig.padding,
        fontSize: sizeConfig.fontSize,
        borderRadius: rounded ? '50px' : borderRadius.medium,
        boxShadow: shadow ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
        position: 'relative',
        overflow: 'hidden',
        '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: 'none',
        },
        '&:focus': {
            outline: 'none',
            boxShadow: 'none',
        },
        '&:focus-visible': {
            outline: 'none',
            boxShadow: 'none',
        },
        ...(animated && {
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                transition: 'left 0.5s',
            },
            '&:hover::before': {
                left: '100%',
            },
        }),
    };
};

// Helper function to get icon button styles
const getIconButtonStyles = (
    buttonVariant: ButtonVariant,
    buttonSize: ButtonSize,
    rounded: boolean,
    shadow: boolean
): SxProps<Theme> => {
    const sizeConfig = getSizeConfig(buttonSize);
    const variantStyles = getVariantStyles(buttonVariant);

    return {
        ...variantStyles,
        width: sizeConfig.minHeight,
        height: sizeConfig.minHeight,
        borderRadius: rounded ? '50%' : borderRadius.medium,
        boxShadow: shadow ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
        '& .MuiSvgIcon-root': {
            fontSize: sizeConfig.iconSize,
        },
        '&:focus': {
            outline: 'none',
            boxShadow: 'none',
        },
        '&:focus-visible': {
            outline: 'none',
            boxShadow: 'none',
        },
    };
};

// Helper function to get FAB styles
const getFabStyles = (
    buttonVariant: ButtonVariant,
    buttonSize: ButtonSize,
    shadow: boolean
): SxProps<Theme> => {
    const sizeConfig = getSizeConfig(buttonSize);
    const variantStyles = getVariantStyles(buttonVariant);

    return {
        ...variantStyles,
        width: sizeConfig.minHeight,
        height: sizeConfig.minHeight,
        boxShadow: shadow ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
        '&:focus': {
            outline: 'none',
            boxShadow: 'none',
        },
        '&:focus-visible': {
            outline: 'none',
            boxShadow: 'none',
        },
    };
};

export const BaseButton: React.FC<BaseButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    buttonType = 'button',
    loading = false,
    loadingText,
    leftIcon,
    rightIcon,
    fullWidth = false,
    rounded = false,
    shadow = false,
    gradient = false,
    animated = false,
    disabled,
    sx,
    ...props
}) => {
    const theme = useTheme();
    const sizeConfig = getSizeConfig(size);

    // Loading state
    if (loading) {
        const loadingContent = loadingText || 'Loading...';
        return (
            <MuiButton
                disabled={true}
                fullWidth={fullWidth}
                sx={[
                    getButtonStyles(variant, size, rounded, shadow, gradient, animated, theme),
                    ...(Array.isArray(sx) ? sx : [sx])
                ]}
                {...props}
            >
                <CircularProgress size={sizeConfig.iconSize} sx={{ mr: 1 }} />
                {loadingContent}
            </MuiButton>
        );
    }

    // Icon Button
    if (buttonType === 'icon') {
        return (
            <IconButton
                disabled={disabled}
                sx={[
                    getIconButtonStyles(variant, size, rounded, shadow),
                    ...(Array.isArray(sx) ? sx : [sx])
                ]}
                {...props}
            >
                {children}
            </IconButton>
        );
    }

    // FAB Button
    if (buttonType === 'fab') {
        return (
            <Fab
                disabled={disabled}
                sx={[
                    getFabStyles(variant, size, shadow),
                    ...(Array.isArray(sx) ? sx : [sx])
                ]}
                {...props}
            >
                {children}
            </Fab>
        );
    }

    // Regular Button
    return (
        <MuiButton
            disabled={disabled}
            fullWidth={fullWidth}
            sx={[
                getButtonStyles(variant, size, rounded, shadow, gradient, animated),
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
            {...props}
        >
            {leftIcon && <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>{leftIcon}</span>}
            {children}
            {rightIcon && <span style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>{rightIcon}</span>}
        </MuiButton>
    );
};

export default BaseButton;
